import React, { useCallback, useEffect, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { Location } from 'history';
import { matchRoutes } from 'react-router-config';
import { Route } from 'react-router';
import RouteDataLoaderContext from './helpers/RouteDataLoaderContext';
import ScrollMemory from './ScrollMemory';
import { useLastLocation } from 'react-router-last-location';
import { useLocation } from 'react-router-dom';

/**
 * Fetches any route preload data while changing to that route on the client-side
 * @see /src/server/ssr.js for server-side implementation
 */
type RouteDataLoaderParams = { children: any; routes: any; store: any };
const RouteDataLoader = ({ children, routes, store }: RouteDataLoaderParams) => {
    const prevRouterLocation = useLastLocation();
    const currRouterLocation = useLocation();
    const initialRender = prevRouterLocation === null;
    const [loadingData, setLoadingData] = useState(false);
    const [loadingLocation, setLoadingLocation] = useState(currRouterLocation);

    const currLocationIsLoading = isEqual(loadingLocation, currRouterLocation);

    const isNewLocation = useCallback(() => {
        const buildUrlFromLocation = ({ hash, pathname, search }) => {
            return `${pathname}${search || ''}${hash || ''}`;
        };

        const currLocation = buildUrlFromLocation(currRouterLocation);
        const prevLocation = prevRouterLocation && buildUrlFromLocation(prevRouterLocation);

        return currLocation !== prevLocation;
    }, [currRouterLocation, prevRouterLocation]);

    const waitingForChunks = !currLocationIsLoading && isNewLocation && !initialRender;

    useEffect(() => {
        // Fetch preload data for current route
        if (!currLocationIsLoading) {
            if (isNewLocation() && !initialRender) {
                setLoadingData(true);
                setLoadingLocation(currRouterLocation);
                // Get all routes that match current location
                const branch = matchRoutes(routes, currRouterLocation.pathname);

                // Preload route chunks for next page
                const preloadPageChunks = branch.map(({ route }) => {
                    return Promise.resolve(route.component.load());
                });

                // Run each matched route's loadData function if it exists
                branch.forEach(({ match, route }) => {
                    if (route.loadData) {
                        route.loadData({ location: currRouterLocation, match, store });
                    }
                });

                // Resolve route preload fetching promises
                Promise.all([...preloadPageChunks])
                    .then(() => setLoadingData(false))
                    .catch((err) => {
                        throw new Error(err);
                    });
            } else {
                loadingData && setLoadingData(false);
            }
        }
    }, [currLocationIsLoading, currRouterLocation, initialRender, isNewLocation, loadingData, routes, store]);

    let usePrevLocation = true;
    if (initialRender) {
        usePrevLocation = false;
    } else if (!loadingData && !waitingForChunks) {
        usePrevLocation = false;
    }

    const renderLocation = usePrevLocation ? prevRouterLocation : currRouterLocation;

    return (
        <Page isLoading={usePrevLocation} location={renderLocation}>
            {children}
        </Page>
    );
};

export default RouteDataLoader;

/**
 * Render the page content of the current route location
 */
type PageProps = {
    children: any;
    isLoading: boolean;
    location: Location;
};
const Page = ({ children, isLoading, location }: PageProps) => (
    <RouteDataLoaderContext.Provider value={isLoading}>
        <ScrollMemory currLocation={location} routeIsPreloading={isLoading} />
        <Route location={location} render={() => children} />
    </RouteDataLoaderContext.Provider>
);
